/**
 * Export data to CSV file
 * 
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the CSV file (without extension)
 * @param {Array} columns - Column definitions [{key, label}]
 */
export function exportToCSV(data, filename, columns) {
    if (!data || data.length === 0) {
        console.warn('No data to export');
        return;
    }

    try {
        // Generate CSV header
        const headers = columns.map(col => col.label || col.key);
        
        // Generate CSV rows
        const rows = data.map(row => {
            return columns.map(col => {
                const value = row[col.key];
                
                // Handle different data types
                if (value === null || value === undefined) {
                    return '';
                }
                
                // Escape commas and quotes in string values
                if (typeof value === 'string') {
                    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                        return `"${value.replace(/"/g, '""')}"`;
                    }
                    return value;
                }
                
                // Handle numbers - remove commas if they're formatting
                if (typeof value === 'number') {
                    return value;
                }
                
                return String(value);
            }).join(',');
        });

        // Combine header and rows
        const csvContent = [headers.join(','), ...rows].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error exporting to CSV:', error);
    }
}

/**
 * Export chart data to CSV (simplified for chart datasets)
 * 
 * @param {Object} chartData - Chart.js data object with labels and datasets
 * @param {string} filename - Name of the CSV file
 */
export function exportChartDataToCSV(chartData, filename) {
    if (!chartData || !chartData.labels) {
        console.warn('No chart data to export');
        return;
    }

    try {
        const { labels, datasets } = chartData;
        
        // Build header row
        const headers = ['Label', ...datasets.map(ds => ds.label || 'Value')];
        
        // Build data rows
        const rows = labels.map((label, index) => {
            const row = [label];
            datasets.forEach(dataset => {
                const value = dataset.data[index];
                row.push(value !== null && value !== undefined ? value : '');
            });
            return row.join(',');
        });

        // Combine and download
        const csvContent = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error exporting chart data to CSV:', error);
    }
}

export default exportToCSV;
